import {
    Brain,
    ChevronDown,
    ChevronRight,
    Eye,
    Loader2,
    Plus,
    Trash2,
    X
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { MgmtApi } from '@/customApiClients/mgmtApiClient/Api';
import { normalizeMemoryPath } from '@/lib/memoryPath';

import { ConfirmDeleteDialog } from '@/components/ConfirmDeleteDialog';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { TableSkeleton } from '@/components/ui/table-skeleton';
import { Textarea } from '@/components/ui/textarea';

interface MemoryStoreRow {
    rowId: string;
    id: string;
    name: string;
    organization?: string | null;
}

interface MemoryFileItem {
    id: string;
    path: string;
}

interface MemoryFileDetail {
    id: string;
    path: string;
    content: string;
    content_size_bytes: number;
}

interface MemoryStoresTabProps {
    mgmtApi: MgmtApi<unknown>;
}

export const MemoryStoresTab: React.FC<MemoryStoresTabProps> = ({
    mgmtApi
}) => {
    const [stores, setStores] = useState<MemoryStoreRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [newName, setNewName] = useState('');
    const [newDescription, setNewDescription] = useState('');
    const [saving, setSaving] = useState(false);
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const [memoryCounts, setMemoryCounts] = useState<Record<string, number>>(
        {}
    );
    const [expandedStoreId, setExpandedStoreId] = useState<string | null>(
        null
    );
    const [filesByStore, setFilesByStore] = useState<
        Record<string, MemoryFileItem[]>
    >({});
    const [loadingFilesId, setLoadingFilesId] = useState<string | null>(null);
    const [newFilePathByStore, setNewFilePathByStore] = useState<
        Record<string, string>
    >({});
    const [creatingFileId, setCreatingFileId] = useState<string | null>(null);
    const [deletingFileId, setDeletingFileId] = useState<string | null>(null);
    const [storeToDelete, setStoreToDelete] = useState<MemoryStoreRow | null>(null);
    const [fileToDelete, setFileToDelete] = useState<{
        storeId: string;
        file: MemoryFileItem;
    } | null>(null);

    const [viewer, setViewer] = useState<
        (MemoryFileDetail & { storeId: string }) | null
    >(null);
    const [viewerLoading, setViewerLoading] = useState(false);
    const [viewerSaving, setViewerSaving] = useState(false);
    const [viewerError, setViewerError] = useState<string | null>(null);

    const fetchMemoryFiles = (storeId: string): Promise<MemoryFileItem[]> =>
        fetch(`/api/memory-stores/${storeId}/memories`, {
            credentials: 'include'
        })
            .then(res => (res.ok ? res.json() : []))
            .then(data => (Array.isArray(data) ? data : []))
            .catch(() => []);

    const fetchStores = () => {
        setLoading(true);
        mgmtApi.api
            .v1ClaudeMemoryStoresList()
            .then(res => {
                const rows = (res?.data || []).map(s => ({
                    rowId: s.id,
                    id: s.anthropicMemoryStoreId,
                    name: s.displayName,
                    organization: s.organization
                }));
                setStores(rows);
                rows.forEach(row => {
                    fetchMemoryFiles(row.id).then(files =>
                        setMemoryCounts(prev => ({
                            ...prev,
                            [row.id]: files.length
                        }))
                    );
                });
            })
            .catch(() => {
                setStores([]);
                setError('Failed to load memory stores');
            })
            .finally(() => setLoading(false));
    };

    useEffect(fetchStores, [mgmtApi]);

    const showSuccess = (msg: string) => {
        toast.success(msg);
    };

    const openNewForm = () => {
        setNewName('');
        setNewDescription('');
        setShowForm(true);
        setError(null);
    };

    const handleCreate = async () => {
        const name = newName.trim();
        if (!name) {
            setError('Name is required');
            return;
        }
        setSaving(true);
        setError(null);
        try {
            const res = await fetch('/api/memory-stores', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name,
                    description: newDescription || undefined
                })
            });
            if (!res.ok) throw new Error('Failed to create memory store');
            const created: { id: string; name: string } = await res.json();

            await mgmtApi.api.v1ClaudeMemoryStoresCreate({
                anthropicMemoryStoreId: created.id,
                displayName: created.name,
                description: newDescription || null
            });

            setShowForm(false);
            fetchStores();
            showSuccess('Memory store created!');
        } catch (e: any) {
            setError(e?.message || 'Failed to create memory store');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (store: MemoryStoreRow) => {
        setDeletingId(store.rowId);
        setError(null);
        try {
            const res = await fetch(`/api/memory-stores/${store.id}`, {
                method: 'DELETE',
                credentials: 'include'
            });
            if (!res.ok) throw new Error('Failed to delete memory store');

            await mgmtApi.api.v1ClaudeMemoryStoresDelete(store.rowId);

            setStores(prev => prev.filter(s => s.rowId !== store.rowId));
            showSuccess('Memory store deleted.');
        } catch (e: any) {
            setError(e?.message || 'Failed to delete memory store');
        } finally {
            setDeletingId(null);
        }
    };

    const toggleExpand = (store: MemoryStoreRow) => {
        if (expandedStoreId === store.id) {
            setExpandedStoreId(null);
            return;
        }
        setExpandedStoreId(store.id);
        if (!filesByStore[store.id]) {
            setLoadingFilesId(store.id);
            fetchMemoryFiles(store.id)
                .then(files => {
                    setFilesByStore(prev => ({ ...prev, [store.id]: files }));
                    setMemoryCounts(prev => ({
                        ...prev,
                        [store.id]: files.length
                    }));
                })
                .finally(() => setLoadingFilesId(null));
        }
    };

    const createMemoryFile = (storeId: string) => {
        const path = normalizeMemoryPath(newFilePathByStore[storeId] || '');
        if (!path) return;
        setCreatingFileId(storeId);
        fetch(`/api/memory-stores/${storeId}/memories`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ path })
        })
            .then(res => (res.ok ? res.json() : null))
            .then((created: MemoryFileItem | null) => {
                if (!created) {
                    setError('Failed to create memory file');
                    return;
                }
                setFilesByStore(prev => ({
                    ...prev,
                    [storeId]: [...(prev[storeId] || []), created]
                }));
                setMemoryCounts(prev => ({
                    ...prev,
                    [storeId]: (prev[storeId] ?? 0) + 1
                }));
                setNewFilePathByStore(prev => ({ ...prev, [storeId]: '' }));
                showSuccess('Memory file created!');
            })
            .finally(() => setCreatingFileId(null));
    };

    const deleteMemoryFile = async (storeId: string, file: MemoryFileItem) => {
        setDeletingFileId(file.id);
        setError(null);
        try {
            const res = await fetch(
                `/api/memory-stores/${storeId}/memories/${file.id}`,
                { method: 'DELETE', credentials: 'include' }
            );
            if (!res.ok) throw new Error('Failed to delete memory file');
            setFilesByStore(prev => ({
                ...prev,
                [storeId]: (prev[storeId] || []).filter(
                    f => f.id !== file.id
                )
            }));
            setMemoryCounts(prev => ({
                ...prev,
                [storeId]: Math.max(0, (prev[storeId] ?? 1) - 1)
            }));
            showSuccess('Memory file deleted.');
        } catch (e: any) {
            setError(e?.message || 'Failed to delete memory file');
        } finally {
            setDeletingFileId(null);
        }
    };

    const openViewer = (storeId: string, file: MemoryFileItem) => {
        setViewerError(null);
        setViewerLoading(true);
        setViewer({
            storeId,
            id: file.id,
            path: file.path,
            content: '',
            content_size_bytes: 0
        });
        fetch(`/api/memory-stores/${storeId}/memories/${file.id}`, {
            credentials: 'include'
        })
            .then(res => {
                if (!res.ok) throw new Error('Failed to load memory file');
                return res.json();
            })
            .then((detail: MemoryFileDetail) =>
                setViewer({ ...detail, storeId })
            )
            .catch(() => setViewerError('Failed to load memory file content'))
            .finally(() => setViewerLoading(false));
    };

    const saveViewer = async () => {
        if (!viewer) return;
        setViewerSaving(true);
        setViewerError(null);
        try {
            const res = await fetch(
                `/api/memory-stores/${viewer.storeId}/memories/${viewer.id}`,
                {
                    method: 'PUT',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ content: viewer.content })
                }
            );
            if (!res.ok) throw new Error('Failed to save memory file');
            const updated: MemoryFileDetail = await res.json();
            setViewer({ ...updated, storeId: viewer.storeId });
            showSuccess('Memory file saved!');
        } catch (e: any) {
            setViewerError(e?.message || 'Failed to save memory file');
        } finally {
            setViewerSaving(false);
        }
    };

    return (
        <div className="bg-card/50 dark:bg-card/80 shadow-xs dark:shadow-lg backdrop-blur-xs border border-border dark:border-border rounded-2xl overflow-hidden">
            <div className="bg-muted/5 dark:bg-muted/10 px-6 py-4 border-border dark:border-border border-b">
                <div className="flex justify-between items-center">
                    <div>
                        <h3 className="font-semibold text-foreground text-lg">
                            Memory Stores
                        </h3>
                        <p className="mt-1 text-muted-foreground text-sm">
                            Persistent memory containers scheduled jobs can
                            read/write across runs.
                        </p>
                    </div>
                    {!showForm && (
                        <Button
                            onClick={openNewForm}
                            variant="tertiary"
                            size="compact"
                            className="gap-2"
                        >
                            <Plus />
                            New Memory Store
                        </Button>
                    )}
                </div>
            </div>

            <div className="space-y-6 p-6">
                {loading && <TableSkeleton columns={5} />}

                {!loading && (
                    <>
                        {showForm && (
                            <div className="bg-muted/5 dark:bg-muted/10 space-y-5 p-5 border border-border rounded-xl">
                                <h4 className="font-semibold text-foreground text-sm">
                                    New Memory Store
                                </h4>
                                <div className="space-y-1.5">
                                    <Label htmlFor="ms-name">
                                        Name
                                        <span className="text-destructive">
                                            {' '}
                                            *
                                        </span>
                                    </Label>
                                    <Input
                                        id="ms-name"
                                        value={newName}
                                        onChange={e =>
                                            setNewName(e.target.value)
                                        }
                                        placeholder="Dataminds Expert"
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <Label htmlFor="ms-description">
                                        Description
                                    </Label>
                                    <Textarea
                                        id="ms-description"
                                        value={newDescription}
                                        onChange={e =>
                                            setNewDescription(e.target.value)
                                        }
                                        placeholder="What this memory is used for — shown to the agent when attached."
                                        className="min-h-18"
                                    />
                                </div>
                                <div className="flex items-center gap-3 pt-2 border-border border-t">
                                    <Button
                                        onClick={handleCreate}
                                        disabled={saving || !newName.trim()}
                                        className="px-6"
                                    >
                                        {saving ? (
                                            <>
                                                <Loader2 className="mr-2 animate-spin" />
                                                Creating...
                                            </>
                                        ) : (
                                            'Create'
                                        )}
                                    </Button>
                                    <Button
                                        onClick={() => setShowForm(false)}
                                        variant="tertiary"
                                    >
                                        <X className="mr-1" />
                                        Cancel
                                    </Button>
                                </div>
                            </div>
                        )}

                        {!showForm && stores.length === 0 && (
                            <EmptyState
                                icon={Brain}
                                title="No memory stores found"
                                description="Create one to attach persistent memory to a scheduled job"
                            />
                        )}

                        {!showForm && stores.length > 0 && (
                            <div>
                                <Table variant="fluid">
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className="w-8" />
                                            <TableHead>Name</TableHead>
                                            <TableHead className="font-mono text-xs">
                                                ID
                                            </TableHead>
                                            <TableHead>Memories</TableHead>
                                            <TableHead className="w-px whitespace-nowrap">
                                                Actions
                                            </TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {stores.map(store => (
                                            <React.Fragment key={store.rowId}>
                                                <TableRow
                                                    className="cursor-pointer"
                                                    onClick={() =>
                                                        toggleExpand(store)
                                                    }
                                                >
                                                    <TableCell>
                                                        {expandedStoreId ===
                                                        store.id ? (
                                                            <ChevronDown className="w-4 h-4 text-muted-foreground" />
                                                        ) : (
                                                            <ChevronRight className="w-4 h-4 text-muted-foreground" />
                                                        )}
                                                    </TableCell>
                                                    <TableCell className="font-medium">
                                                        {store.name}
                                                    </TableCell>
                                                    <TableCell className="font-mono text-muted-foreground text-xs">
                                                        {store.id}
                                                    </TableCell>
                                                    <TableCell className="text-muted-foreground text-sm">
                                                        {memoryCounts[
                                                            store.id
                                                        ] ?? (
                                                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                        )}
                                                    </TableCell>
                                                    <TableCell className="text-right">
                                                        <Button
                                                            variant="destructive"
                                                            size="compact"
                                                            onClick={e => {
                                                                e.stopPropagation();
                                                                setStoreToDelete(store);
                                                            }}
                                                            disabled={
                                                                deletingId ===
                                                                store.rowId
                                                            }
                                                            className="gap-1.5"
                                                        >
                                                            {deletingId ===
                                                            store.rowId ? (
                                                                <Loader2 className="animate-spin" />
                                                            ) : (
                                                                <Trash2 />
                                                            )}
                                                            Delete
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                                {expandedStoreId ===
                                                    store.id && (
                                                    <TableRow className="bg-muted/5 dark:bg-muted/10">
                                                        <TableCell />
                                                        <TableCell
                                                            colSpan={4}
                                                            className="py-3"
                                                        >
                                                            {loadingFilesId ===
                                                            store.id ? (
                                                                <div className="flex items-center gap-2 text-muted-foreground text-sm">
                                                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                                    Loading
                                                                    memory
                                                                    files...
                                                                </div>
                                                            ) : (
                                                                <div className="space-y-1.5">
                                                                    {(
                                                                        filesByStore[
                                                                            store
                                                                                .id
                                                                        ] ||
                                                                        []
                                                                    ).length ===
                                                                        0 && (
                                                                        <p className="text-muted-foreground text-sm">
                                                                            No
                                                                            memory
                                                                            files
                                                                            yet.
                                                                        </p>
                                                                    )}
                                                                    {(
                                                                        filesByStore[
                                                                            store
                                                                                .id
                                                                        ] ||
                                                                        []
                                                                    ).map(
                                                                        file => (
                                                                            <div
                                                                                key={
                                                                                    file.id
                                                                                }
                                                                                className="flex justify-between items-center gap-3 bg-background/60 px-3 py-1.5 border border-border/60 rounded-lg"
                                                                            >
                                                                                <span className="font-mono text-xs">
                                                                                    {
                                                                                        file.path
                                                                                    }
                                                                                </span>
                                                                                <div className="flex items-center gap-1">
                                                                                    <Button
                                                                                        variant="ghost"
                                                                                        size="compact"
                                                                                        className="gap-1.5"
                                                                                        onClick={() =>
                                                                                            openViewer(
                                                                                                store.id,
                                                                                                file
                                                                                            )
                                                                                        }
                                                                                    >
                                                                                        <Eye />
                                                                                        View
                                                                                    </Button>
                                                                                    <Button
                                                                                        variant="destructive"
                                                                                        size="compact"
                                                                                        className="gap-1.5"
                                                                                        onClick={() =>
 setFileToDelete({ storeId: store.id, file })
 }
                                                                                        disabled={
                                                                                            deletingFileId ===
                                                                                            file.id
                                                                                        }
                                                                                    >
                                                                                        {deletingFileId ===
                                                                                        file.id ? (
                                                                                            <Loader2 className="animate-spin" />
                                                                                        ) : (
                                                                                            <Trash2 />
                                                                                        )}
                                                                                        Delete
                                                                                    </Button>
                                                                                </div>
                                                                            </div>
                                                                        )
                                                                    )}
                                                                    <div
                                                                        className="flex items-center gap-2 pt-1"
                                                                        onClick={e =>
                                                                            e.stopPropagation()
                                                                        }
                                                                    >
                                                                        <Input
                                                                            value={
                                                                                newFilePathByStore[
                                                                                    store
                                                                                        .id
                                                                                ] ||
                                                                                ''
                                                                            }
                                                                            onChange={e =>
                                                                                setNewFilePathByStore(
                                                                                    prev => ({
                                                                                        ...prev,
                                                                                        [store.id]:
                                                                                            e
                                                                                                .target
                                                                                                .value
                                                                                    })
                                                                                )
                                                                            }
                                                                            placeholder="weekly-summary"
                                                                            className="flex-1 h-8"
                                                                            onKeyDown={e => {
                                                                                if (
                                                                                    e.key ===
                                                                                    'Enter'
                                                                                )
                                                                                    createMemoryFile(
                                                                                        store.id
                                                                                    );
                                                                            }}
                                                                        />
                                                                        <Button
                                                                            variant="tertiary"
                                                                            size="compact"
                                                                            className="gap-1.5 h-8"
                                                                            onClick={() =>
                                                                                createMemoryFile(
                                                                                    store.id
                                                                                )
                                                                            }
                                                                            disabled={
                                                                                !(
                                                                                    newFilePathByStore[
                                                                                        store
                                                                                            .id
                                                                                    ] ||
                                                                                    ''
                                                                                ).trim() ||
                                                                                creatingFileId ===
                                                                                    store.id
                                                                            }
                                                                        >
                                                                            {creatingFileId ===
                                                                            store.id ? (
                                                                                <Loader2 className="animate-spin" />
                                                                            ) : (
                                                                                <Plus />
                                                                            )}
                                                                            New
                                                                            file
                                                                        </Button>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        )}

                        {error && (
                            <p className="text-destructive text-caption">
                                {error}
                            </p>
                        )}
                    </>
                )}
            </div>

            <Dialog
                open={!!viewer}
                onOpenChange={open => {
                    if (!open) setViewer(null);
                }}
            >
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="font-mono">
                            {viewer?.path}
                        </DialogTitle>
                    </DialogHeader>
                    {viewerLoading ? (
                        <Skeleton className="rounded-lg h-80" />
                    ) : (
                        <div className="space-y-3">
                            <Textarea
                                value={viewer?.content ?? ''}
                                onChange={e =>
                                    setViewer(prev =>
                                        prev
                                            ? {
                                                  ...prev,
                                                  content: e.target.value
                                              }
                                            : prev
                                    )
                                }
                                className="min-h-80 font-mono"
                            />
                            {viewerError && (
                                <p className="text-destructive text-caption">
                                    {viewerError}
                                </p>
                            )}
                        </div>
                    )}
                    <DialogFooter>
                        <Button size="medium"
                            variant="ghost"
                            onClick={() => setViewer(null)}
                        >
                            Close
                        </Button>
                        <Button size="medium"
                            onClick={saveViewer}
                            disabled={viewerLoading || viewerSaving}
                        >
                            {viewerSaving ? (
                                <>
                                    <Loader2 className="mr-2 animate-spin" />
                                    Saving...
                                </>
                            ) : (
                                'Save'
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <ConfirmDeleteDialog
                open={!!storeToDelete}
                onOpenChange={open => !open && setStoreToDelete(null)}
                title="Delete memory store"
                description={
                    <>
                        This will permanently delete{' '}
                        <span className="font-medium text-foreground">
                            {storeToDelete?.name}
                        </span>{' '}
                        and every memory file in it. This action cannot be
                        undone.
                    </>
                }
                onConfirm={async () => {
                    if (storeToDelete) await handleDelete(storeToDelete);
                    setStoreToDelete(null);
                }}
            />

            <ConfirmDeleteDialog
                open={!!fileToDelete}
                onOpenChange={open => !open && setFileToDelete(null)}
                title="Delete memory file"
                description={
                    <>
                        This will permanently delete{' '}
                        <span className="font-medium text-foreground">
                            {fileToDelete?.file.path}
                        </span>
                        . This action cannot be undone.
                    </>
                }
                onConfirm={async () => {
                    if (fileToDelete) {
                        await deleteMemoryFile(
                            fileToDelete.storeId,
                            fileToDelete.file
                        );
                    }
                    setFileToDelete(null);
                }}
            />
        </div>
    );
};
